import { DEFAULT_FLAGS } from "@/utils/featureFlags"
import { Tour } from "../types/tour.types"

const DEMO_QUESTIONNAIRE_BASE_ROUTE =
  "/questionnaire/00000000-0000-0000-0000-000000000000"
const DEMO_EXCEL_QUESTIONS_ROUTE = `${DEMO_QUESTIONNAIRE_BASE_ROUTE}/excel-questions`
const DEMO_EXCEL_ANSWERS_ROUTE = `${DEMO_QUESTIONNAIRE_BASE_ROUTE}/excel-answers`
const DEMO_PREVIEW_ROUTE = `${DEMO_QUESTIONNAIRE_BASE_ROUTE}/preview`

export const INITIAL_ONBOARDING_TOUR: Tour = {
  id: "questionnaire",
  name: "Questionnaire Tour",
  storageKey: "turnus-tour-questionnaire-completed",
  steps: [
    {
      id: "new-inquiry-step",
      targetElementId: "navItem1",
      title: "TOUR/QUESTIONNAIRE/NEW_INQUIRY/TITLE",
      description: "TOUR/QUESTIONNAIRE/NEW_INQUIRY/DESCRIPTION",
      nextRoute: "/questionnaire/upload",
      position: "right",
    },
    {
      id: "upload-file-step",
      targetElementId: "file-upload-zone",
      title: "TOUR/QUESTIONNAIRE/UPLOAD/TITLE",
      description: "TOUR/QUESTIONNAIRE/UPLOAD/DESCRIPTION",
      position: "right",
      showNextButton: true,
      route: "/questionnaire/upload",
      nextRoute: DEMO_EXCEL_QUESTIONS_ROUTE,
      padding: 10,
    },
    {
      id: "extraction-animation-step",
      targetElementId: "question-extraction-loading",
      title: "TOUR/QUESTIONNAIRE/EXTRACTION/TITLE",
      description: "TOUR/QUESTIONNAIRE/EXTRACTION/DESCRIPTION",
      position: "right",
      skipOnBackNavigation: true,
      hideFromStepCount: true,
      route: DEMO_EXCEL_QUESTIONS_ROUTE,
    },
    {
      id: "generate-answers-step",
      targetElementId: "generate-answers-button",
      title: "TOUR/QUESTIONNAIRE/GENERATE/TITLE",
      description: "TOUR/QUESTIONNAIRE/GENERATE/DESCRIPTION",
      position: "topLeft",
      route: DEMO_EXCEL_QUESTIONS_ROUTE,
    },
    {
      id: "question-answering-animation-step",
      targetElementId: "question-answering-loading",
      title: "TOUR/QUESTIONNAIRE/ANSWERING/TITLE",
      description: "TOUR/QUESTIONNAIRE/ANSWERING/DESCRIPTION",
      position: "right",
      skipOnBackNavigation: true,
      hideFromStepCount: true,
      route: DEMO_EXCEL_QUESTIONS_ROUTE,
      nextRoute: DEMO_EXCEL_ANSWERS_ROUTE,
    },
    {
      id: "celebrate-answer-step",
      targetElementId: "total-questions-answers",
      title: "TOUR/QUESTIONNAIRE/CELEBRATE/TITLE",
      description: "TOUR/QUESTIONNAIRE/CELEBRATE/DESCRIPTION",
      position: "bottom",
      showNextButton: true,
      route: DEMO_EXCEL_ANSWERS_ROUTE,
      padding: 8,
    },
    {
      id: "first-answer-step",
      targetElementId: "first-answer-field",
      title: "TOUR/QUESTIONNAIRE/REVIEW/TITLE",
      description: "TOUR/QUESTIONNAIRE/REVIEW/DESCRIPTION",
      position: "bottom",
      showNextButton: true,
      route: DEMO_EXCEL_ANSWERS_ROUTE,
      padding: 5,
    },
    {
      id: "first-answer-source-step",
      targetElementId: "first-answer-source-field",
      title: "TOUR/QUESTIONNAIRE/SOURCE/TITLE",
      description: "TOUR/QUESTIONNAIRE/SOURCE/DESCRIPTION",
      position: "bottom",
      showNextButton: true,
      route: DEMO_EXCEL_ANSWERS_ROUTE,
      padding: 5,
    },
    {
      id: "done-answers-step",
      targetElementId: "done-answers-button",
      title: "TOUR/QUESTIONNAIRE/DONE/TITLE",
      description: "TOUR/QUESTIONNAIRE/DONE/DESCRIPTION",
      position: "topLeft",
      route: DEMO_EXCEL_ANSWERS_ROUTE,
      nextRoute: DEMO_PREVIEW_ROUTE,
    },
    {
      id: "download-file-step",
      targetElementId: "download-file-button",
      title: "TOUR/QUESTIONNAIRE/DOWNLOAD/TITLE",
      description: "TOUR/QUESTIONNAIRE/DOWNLOAD/DESCRIPTION",
      position: "left",
      showNextButton: true,
      route: DEMO_PREVIEW_ROUTE,
    },
  ],
}

export const createAiChatTour = (chatHistoryEnabled: boolean): Tour => ({
  id: "ai-chat",
  name: "AI Chat Tour",
  storageKey: "turnus-tour-ai-chat-completed",
  steps: [
    {
      id: "chat-nav-step",
      targetElementId: chatHistoryEnabled
        ? "ai-chat-new-conv-button"
        : "navItem4",
      title: "TOUR/AI_CHAT/NAV/TITLE",
      description: "TOUR/AI_CHAT/NAV/DESCRIPTION",
      nextRoute: "/chat",
      position: "right",
    },
    {
      id: "chat-input-step",
      targetElementId: "chat-input-step",
      title: "TOUR/AI_CHAT/INPUT/TITLE",
      description: "TOUR/AI_CHAT/INPUT/DESCRIPTION",
      position: "top",
      route: "/chat",
    },
    {
      id: "chat-thinking-step",
      targetElementId: "chat-thinking-indicator",
      title: "TOUR/AI_CHAT/THINKING/TITLE",
      description: "TOUR/AI_CHAT/THINKING/DESCRIPTION",
      position: "bottom",
      autoAdvanceDelay: 3000,
      hideFromStepCount: true,
      route: "/chat",
      padding: 10,
    },
    {
      id: "chat-response-step",
      targetElementId: "chat-response-area",
      title: "TOUR/AI_CHAT/RESPONSE/TITLE",
      description: "TOUR/AI_CHAT/RESPONSE/DESCRIPTION",
      position: "bottom",
      route: "/chat",
      padding: 10,
    },
  ],
})

export const AI_CHAT_TOUR: Tour = createAiChatTour(DEFAULT_FLAGS.CHAT_HISTORY)

export const DATABASE_TOUR: Tour = {
  id: "database",
  name: "Knowledge Base Tour",
  storageKey: "turnus-tour-database-completed",
  steps: [
    {
      id: "database-nav-step",
      targetElementId: "navItem3",
      title: "TOUR/DATABASE/NAV/TITLE",
      description: "TOUR/DATABASE/NAV/DESCRIPTION",
      nextRoute: "/knowledgebase",
      position: "right",
    },
    {
      id: "database-files-step",
      targetElementId: "knowledgebase-file-list",
      title: "TOUR/DATABASE/FILES/TITLE",
      description: "TOUR/DATABASE/FILES/DESCRIPTION",
      position: "bottom",
      showNextButton: true,
      route: "/knowledgebase",
    },
    {
      id: "database-upload-step",
      targetElementId: "knowledgebase-upload-zone",
      title: "TOUR/DATABASE/UPLOAD/TITLE",
      description: "TOUR/DATABASE/UPLOAD/DESCRIPTION",
      position: "topLeft",
      showNextButton: true,
      route: "/knowledgebase",
    },
    {
      id: "database-complete-step",
      targetElementId: "knowledgebase-info-box",
      title: "TOUR/DATABASE/COMPLETE/TITLE",
      description: "TOUR/DATABASE/COMPLETE/DESCRIPTION",
      position: "bottom",
      route: "/knowledgebase",
    },
  ],
}

export const createAllTours = (flags: { CHAT_HISTORY: boolean }): Tour[] => [
  INITIAL_ONBOARDING_TOUR,
  createAiChatTour(flags.CHAT_HISTORY),
  DATABASE_TOUR,
]

export const ALL_TOURS: Tour[] = createAllTours(DEFAULT_FLAGS)
